import React, { createContext, type ReactNode, useEffect, useState } from 'react'

import type { RelatedProduct } from '../../../../types/RelatedProduct'
import type { WorkId } from '../../../../types/WorkId'
import { useWorksDataContext } from '../WorksDataContext'
import { useRelatedProductsQuery } from '../../../../hooks/queries/useRelatedProductsQuery'

export interface IRelatedProductsContext {
  filteredRelatedProducts: RelatedProduct[] | null
  hasRelatedProductsToShow: boolean
  isFetched: boolean
  isLoading: boolean
}

export const RelatedProductsContext = createContext<IRelatedProductsContext>({} as IRelatedProductsContext)

const RelatedProductsContextProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const { worksData } = useWorksDataContext()
  const [filteredRelatedProducts, setFilteredRelatedProducts] = useState<RelatedProduct[] | null>(null)

  const { data: relatedProducts, isFetched, isLoading } = useRelatedProductsQuery()

  useEffect(() => {
    if (isFetched && relatedProducts?.length) {
      const relatedWorkIds = relatedProducts.filter((x) => x.workId !== undefined).map((x) => x.workId) as WorkId[]
      const relatedProductsToHide = Object.keys(worksData).filter(
        (w) => worksData[w] && worksData[w].isDeleted !== true && relatedWorkIds.some((relatedWorkId) => w === relatedWorkId),
      )
      const filtered = relatedProducts.filter((rp) => !relatedProductsToHide.some((workId) => rp.workId === workId))
      setFilteredRelatedProducts(filtered)
    } else if (isFetched) {
      setFilteredRelatedProducts([])
    }
  }, [isFetched, relatedProducts, worksData])

  const hasRelatedProductsToShow = (relatedProducts?.length ?? 0) > 0

  return (
    <RelatedProductsContext.Provider
      value={{
        filteredRelatedProducts,
        hasRelatedProductsToShow,
        isFetched,
        isLoading,
      }}
    >
      {children}
    </RelatedProductsContext.Provider>
  )
}

export default RelatedProductsContextProvider
